import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AppUser {
  id: string;
  email: string;
  name: string;
  role: 'user' | 'admin';
}

interface AuthContextType {
  user: AppUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ error: string | null; role?: 'user' | 'admin' }>;
  register: (email: string, password: string, name: string) => Promise<{ error: string | null; needsConfirmation?: boolean }>;
  logout: () => void;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ─── Mock auth helpers ────────────────────────────────────────────────────────

const MOCK_SESSION_KEY = 'journey_mapper_mock_user';

function getMockUser(email: string): AppUser {
  return {
    id: `mock-${email}`,
    email,
    name: email.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    role: email === 'admin@admin.com' ? 'admin' : 'user',
  };
}

function loadMockSession(): AppUser | null {
  try {
    const raw = localStorage.getItem(MOCK_SESSION_KEY);
    return raw ? (JSON.parse(raw) as AppUser) : null;
  } catch {
    return null;
  }
}

function saveMockSession(user: AppUser | null) {
  if (user) {
    localStorage.setItem(MOCK_SESSION_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(MOCK_SESSION_KEY);
  }
}

// ─── Provider ────────────────────────────────────────────────────────────────

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  // ── Init ──
  useEffect(() => {
    if (!isSupabaseConfigured) {
      const saved = loadMockSession();
      setUser(saved);
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        const u = session.user;
        setUser({
          id: u.id,
          email: u.email ?? '',
          name: u.user_metadata?.name ?? u.email?.split('@')[0] ?? 'Uživatel',
          role: (u.user_metadata?.role === 'admin' || (u.app_metadata as any)?.role === 'admin') ? 'admin' : 'user',
        });
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        const u = session.user;
        setUser({
          id: u.id,
          email: u.email ?? '',
          name: u.user_metadata?.name ?? u.email?.split('@')[0] ?? 'Uživatel',
          role: (u.user_metadata?.role === 'admin' || (u.app_metadata as any)?.role === 'admin') ? 'admin' : 'user',
        });
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // ── Login ──
  const login = async (email: string, password: string): Promise<{ error: string | null; role?: 'user' | 'admin' }> => {
    if (!isSupabaseConfigured) {
      if (!password) return { error: 'Zadejte heslo' };
      const mockUser = getMockUser(email);
      setUser(mockUser);
      saveMockSession(mockUser);
      return { error: null, role: mockUser.role };
    }

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };

    let role: 'user' | 'admin' = 'user';
    if (data.session) {
      const { data: refreshed } = await supabase.auth.refreshSession();
      if (refreshed.user) {
        const u = refreshed.user;
        role = (u.user_metadata?.role === 'admin' || (u.app_metadata as any)?.role === 'admin') ? 'admin' : 'user';
        setUser({
          id: u.id,
          email: u.email ?? '',
          name: u.user_metadata?.name ?? u.email?.split('@')[0] ?? 'Uživatel',
          role,
        });
      }
    }
    return { error: null, role };
  };

  // ── Register ──
  const register = async (
    email: string,
    password: string,
    name: string,
  ): Promise<{ error: string | null; needsConfirmation?: boolean }> => {
    if (!isSupabaseConfigured) {
      const mockUser = getMockUser(email);
      mockUser.name = name || mockUser.name;
      setUser(mockUser);
      saveMockSession(mockUser);
      return { error: null };
    }

    // KLÍČOVÁ ZMĚNA: emailRedirectTo posíláme explicitně, aby Supabase
    // přesměrovala uživatele po kliknutí v confirmation emailu na NÁŠ
    // /auth/callback, a ne na Site URL z dashboardu (což bývá localhost).
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) return { error: error.message };

    // Pokud má projekt zapnutou email confirmation, session bude null
    // a uživatel musí kliknout v emailu. Pokud confirmation vypnuta,
    // session přijde rovnou.
    const needsConfirmation = !data?.session;
    return { error: null, needsConfirmation };
  };

  // ── Logout ──
  const logout = () => {
    if (!isSupabaseConfigured) {
      setUser(null);
      saveMockSession(null);
      return;
    }
    supabase.auth.signOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
